import { FilterToolbarConfig } from '@shared/components/generic-filter-toolbar';
import { ENROLMENT_STATUS_OPTIONS } from '@shared/enums/enrolment.enum';
import { SHIFT_OPTIONS } from '@shared/enums/shift.enum';
import { CourseLevelApiService, ClusterApiService } from '@shared/services';

/**
 * Filter configuration for enrolment list
 * Supports: text search, date ranges, levels, statuses, shifts, and clusters
 *
 * NOTE: Services must be passed as parameters instead of using inject()
 * because mapFunctions() in generic-p-table uses JSON.parse(JSON.stringify())
 * which destroys service instances in nested objects
 */
export const enrolmentFilterConfig = (
    courseLevelApiService: CourseLevelApiService,
    clusterApiService: ClusterApiService
): FilterToolbarConfig => {

    return {
        fields: [
            // Text search field (global - updates immediately)
            {
                key: 'searchText',
                label: 'Tìm kiếm',
                type: 'text',
                location: 'global',
                placeholder: 'Tìm theo mã HV, tên HV, tên EC, mã EC...',
                width: 'w-96',
                debounceTime: 300
            },

            // Available date range (popover)
            {
                key: 'availableFrom',
                label: 'Từ ngày',
                type: 'dateRange',
                location: 'popover',
                width: 'w-96'
            },

            // Deadline 1 date range (popover)
            {
                key: 'deadline1',
                label: 'Deadline 1',
                type: 'dateRange',
                location: 'popover',
                width: 'w-96'
            },

            // Deadline 2 date range (popover)
            {
                key: 'deadline2',
                label: 'Deadline 2',
                type: 'dateRange',
                location: 'popover',
                width: 'w-96'
            },

            // Level multi-select (API-based, popover)
            {
                key: 'levelIds',
                label: 'Level',
                type: 'multiSelect',
                location: 'popover',
                isArrayField: false,
                width: 'w-80',
                selectConfig: {
                    service: courseLevelApiService,
                    method: 'getOptions',
                    placeholder: 'Chọn level',
                    optionLabel: 'name',
                    optionValue: 'id',
                    sortField: 'code,asc',
                    searchFields: ['name', 'code']
                }
            },

            // Status multi-select (popover)
            {
                key: 'statuses',
                label: 'Trạng thái',
                type: 'multiSelect',
                location: 'popover',
                isArrayField: false,
                width: 'w-80',
                selectConfig: {
                    staticOptions: ENROLMENT_STATUS_OPTIONS,
                    placeholder: 'Chọn trạng thái',
                    optionLabel: 'label',
                    optionValue: 'value'
                }
            },

            // Available Shifts with priority (popover)
            {
                key: 'availableShifts',
                label: 'Ca học',
                type: 'multiSelectPriority',
                location: 'popover',
                width: 'w-full',
                selectConfig: {
                    staticOptions: SHIFT_OPTIONS,
                    placeholder: 'Chọn ca học',
                    optionLabel: 'label',
                    optionValue: 'value'
                },
                priorityModeOptions: [
                    { label: 'Có', value: 'positive' },
                    { label: 'Chưa set', value: 'undefined' },
                    { label: 'Không', value: 'zero' },
                ]
            },

            // Available Clusters with priority (API-based, popover)
            {
                key: 'availableClusterIds',
                label: 'Cụm',
                type: 'multiSelectPriority',
                location: 'popover',
                width: 'w-full',
                selectConfig: {
                    service: clusterApiService,
                    method: 'getOptions',
                    placeholder: 'Chọn cụm',
                    optionLabel: 'name',
                    optionValue: 'id',
                    sortField: 'code,asc',
                    searchFields: ['name', 'code']
                },
                priorityModeOptions: [
                    { label: 'Có', value: 'positive' },
                    { label: 'Chưa set', value: 'undefined' },
                    { label: 'Không', value: 'zero' },
                ]
            }
        ],
        showClearButton: true,
        showApplyButton: false, // Auto-apply on change
        collapsible: true,
        defaultCollapsed: false,

        // Filter view panel
        entityType: 'enrollments',
        showFilterViewPanel: true
    };
};

